import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Collapse, useTheme } from '@mui/material';
import classNames from 'classnames';
import toast from 'react-hot-toast';
import axios, { AxiosError } from 'axios';
import { useProfessions } from '../../hooks/useProfessions';
import { IProfession, ISubProfession } from '../../models/Profession.interface';
import axiosInstant from '../../api';
import { SingleProfessionEndpoint } from '../../api/endpoints';
import { DotLottleLoader } from '../../components/Loader';
import {
  ActionIconButton,
  FlashWrapper,
  InlineInput,
  getProfessionColor,
} from './constants';
import { SubProfessionListItem } from './SubProfessionListItem';
import { AddListItem } from './AddListItem';
import { ConfirmationDialog } from '../../components/Modal/ConfirmationDialog';
import { IconTrash, IconPencil, IconChevronDown, IconConfirm, IconClose } from '../../utils/icons';
import { useThemeTokens } from '../../hooks/useThemeTokens';

interface Props {
  profession: IProfession;
  onUpdate(professionId: number, newItem: IProfession | null): void;
  isNew?: boolean;
}

export const ProfessionListItem: FC<Props> = ({ profession, onUpdate, isNew }) => {
  const theme = useTheme();
  const myRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { subProfessionLibrary, getProfessions } = useProfessions();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newName, setNewName] = useState(profession.name);
  const [flash, setFlash] = useState<'' | 'green' | 'red'>('');
  const [newOne, setNewOne] = useState<number | undefined>();
  const [subProfessions, setSubProfessions] = useState<Array<ISubProfession>>(
    subProfessionLibrary[profession.id] ?? [],
  );

  const { mode } = useThemeTokens();
  const color = getProfessionColor(profession.id, mode === 'dark');

  useEffect(() => {
    if (isNew) {
      myRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlash('green');
      setTimeout(() => setFlash(''), theme.transitions.duration.standard * 2);
    }
  }, []);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const handleDeleteProfession = async () => {
    setLoading(true);
    try {
      await axiosInstant.delete(SingleProfessionEndpoint(profession.id));
      setDeleting(false);
      toast.success(`Profession '${profession.name}' Removed!`);
      setTimeout(() => onUpdate(profession.id, null), 100);
      getProfessions();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update');
      setFlash('red');
    }
  };

  const handleNameUpdate = async () => {
    setLoading(true);
    try {
      const response = await axiosInstant.patch(SingleProfessionEndpoint(profession.id), { name: newName });
      onUpdate(profession.id, response.data);
      toast.success('Profession successfully updated!');
      setFlash('green');
      setEditing(false);
      getProfessions();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update');
      setFlash('red');
    }
    setLoading(false);
    setTimeout(() => setFlash(''), theme.transitions.duration.standard);
  };

  const handleUpdateCancel = () => {
    setEditing(false);
    setNewName(profession.name);
  };

  const handleSubProfessionUpdate = (subProfessionId: number, newItem: ISubProfession | null) => {
    const next = [...subProfessions];
    const index = next.findIndex((sp) => sp.id === subProfessionId);
    if (newItem === null) {
      next.splice(index, 1);
    } else {
      next[index] = newItem;
      next.sort((a, b) => a.name.localeCompare(b.name));
    }
    setSubProfessions(next);
    getProfessions();
  };

  const handleAddSubProfession = async (newItem: { name: string; order?: number }) => {
    try {
      const response = await axiosInstant.post(`${SingleProfessionEndpoint(profession.id)}/subprofessions`, newItem);
      const { data } = response;
      setNewOne(data.id);
      const next = [...subProfessions, data];
      next.sort((a, b) => a.name.localeCompare(b.name));
      setSubProfessions(next);
      getProfessions();
      toast.success(`Sub-profession '${newItem.name}' added!`);
      return '';
    } catch (e) {
      const error = e as AxiosError;
      if (axios.isAxiosError(error)) {
        console.error(error.response?.data);
        toast.error(error.response?.data as string);
        return error.response?.data as string;
      }
      console.error(error);
      toast.error('Failed to create new sub-profession');
      return 'Failed to create new sub-profession';
    }
  };

  return (
    <>
      <div
        id={`profession-${profession.id}`}
        className={classNames(
          // hover:border-[#D5D0C8] stays literal: no token expresses "one step
          // stronger than --border", and --muted would darken light mode.
          'bg-surface border border-border-token rounded-[10px] overflow-hidden transition-all hover:border-[#D5D0C8]',
          open && 'shadow-[shadow:var(--sh-1)]',
        )}
      >
        <FlashWrapper ref={myRef} flash={flash}>
          <div
            className={classNames(
              'flex items-center gap-2.5 px-4 py-3.5 sm:px-4 sm:py-3.5',
              !editing && 'cursor-pointer',
            )}
            onClick={() => { if (!editing) setOpen(!open); }}
          >
            <div className="w-2 h-2 rounded-[3px] shrink-0" style={{ background: color }} />

            {editing ? (
              <InlineInput
                ref={inputRef}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleNameUpdate();
                  if (e.key === 'Escape') handleUpdateCancel();
                }}
              />
            ) : (
              <span className="flex-1 min-w-0 truncate text-base text-text-1">{profession.name}</span>
            )}

            {!editing && subProfessions.length > 0 && (
              <span className="text-[10.5px] font-semibold text-text-2 bg-surface-2 px-2 py-0.5 rounded-[10px] shrink-0">
                {subProfessions.length}
              </span>
            )}

            <div
              className="flex items-center gap-0.5 ml-auto shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              {!loading && !editing && (
                <>
                  <ActionIconButton title="Edit" onClick={() => setEditing(true)}>
                    <IconPencil size={14} stroke="currentColor" />
                  </ActionIconButton>
                  <ActionIconButton title="Delete" onClick={() => setDeleting(true)}>
                    <IconTrash width={14} height={14} stroke="currentColor" />
                  </ActionIconButton>
                  <ActionIconButton title={open ? 'Collapse' : 'Expand'} onClick={() => setOpen(!open)}>
                    <IconChevronDown open={open} />
                  </ActionIconButton>
                </>
              )}
              {!loading && editing && (
                <>
                  <ActionIconButton tone="confirm" title="Save" onClick={handleNameUpdate}>
                    <IconConfirm />
                  </ActionIconButton>
                  <ActionIconButton title="Cancel" onClick={handleUpdateCancel}>
                    <IconClose />
                  </ActionIconButton>
                </>
              )}
              {loading && <DotLottleLoader sx={{ width: 28, height: 28 }} fill={false} />}
            </div>
          </div>
        </FlashWrapper>

        <Collapse in={open} timeout="auto">
          <div className="border-t border-border-token pb-1.5">
            {subProfessions?.map((subProfession) => (
              <SubProfessionListItem
                key={subProfession.id}
                subProfession={subProfession}
                parentColor={color}
                onUpdate={handleSubProfessionUpdate}
                isNew={newOne === subProfession.id}
              />
            ))}
            <AddListItem
              label="Add Sub-Profession"
              parentColor={color}
              onSave={handleAddSubProfession}
            />
          </div>
        </Collapse>
      </div>

      <ConfirmationDialog
        title="Delete Profession"
        description={`Are you sure you want to delete '${profession.name}' and all related sub-professions? This action cannot be undone.`}
        show={deleting}
        onConfirm={handleDeleteProfession}
        onCancel={() => setDeleting(false)}
        confirmButton={({ onClick }) => (
          <Button color="red" startIcon={<IconTrash width={24} height={24} stroke={theme.palette.common.white} />} onClick={onClick}>
            Delete Profession
          </Button>
        )}
      />
    </>
  );
};
